import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { systemSettingsService } from '../services/api';
import { useAuth } from './AuthContext';
import { formatDate as formatDateHelper, formatTime as formatTimeHelper, formatDateTime as formatDateTimeHelper } from '../utils/dateFormat';

// Create SystemSettings Context
const SystemSettingsContext = createContext();

// Default general settings
const defaultGeneralSettings = {
  siteName: 'Bunny Boba',
  siteDescription: 'Premium Bubble Tea Franchise',
  timezone: 'America/New_York',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '12h',
  language: 'en',
  theme: 'light',
  gstPercentage: 18.0,
  taxPercentage: 5.0,
  enableGST: true,
  enableTax: true,
  taxMethod: 'exclusive'
};

// SystemSettings Provider Component
export const SystemSettingsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [generalSettings, setGeneralSettings] = useState(defaultGeneralSettings);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);

  // Fetch general settings from API
  const fetchGeneralSettings = useCallback(async () => {
    // For website, we load settings even without auth for public display
    try {
      setLoading(true);
      setError(null);
      const response = await systemSettingsService.getGeneralSettings();
      const settings = response?.data?.data;
      
      if (settings) {
        setGeneralSettings({
          ...defaultGeneralSettings,
          ...settings
        });
      }
    } catch (err) {
      console.error('[SystemSettingsContext] Error fetching general settings:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh general settings (call when settings are updated)
  const refresh = useCallback(() => {
    fetchGeneralSettings();
  }, [fetchGeneralSettings]);

  // Format date using current settings
  const formatDate = useCallback((value) => {
    return formatDateHelper(value, generalSettings.dateFormat);
  }, [generalSettings.dateFormat]);

  // Format time using current settings
  const formatTime = useCallback((value) => {
    return formatTimeHelper(value, generalSettings.timeFormat);
  }, [generalSettings.timeFormat]);

  // Format datetime using current settings
  const formatDateTime = useCallback((value) => {
    return formatDateTimeHelper(value, generalSettings.dateFormat, generalSettings.timeFormat);
  }, [generalSettings.dateFormat, generalSettings.timeFormat]);

  // Initialize settings on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      console.log('[SystemSettingsContext] Initializing, fetching general settings...');
      hasInitialized.current = true;
      fetchGeneralSettings();
    }
  }, [fetchGeneralSettings]);

  // Re-fetch when user authentication changes (they may have different permissions)
  useEffect(() => {
    if (hasInitialized.current && isAuthenticated) {
      console.log('[SystemSettingsContext] User authenticated, refreshing settings...');
      fetchGeneralSettings();
    }
  }, [isAuthenticated, fetchGeneralSettings]);

  const contextValue = {
    // Settings data
    generalSettings,
    dateFormat: generalSettings.dateFormat,
    timeFormat: generalSettings.timeFormat,
    // State
    loading,
    error,
    // Formatters
    formatDate,
    formatTime,
    formatDateTime,
    // Actions
    refresh,
    fetchGeneralSettings
  };

  return (
    <SystemSettingsContext.Provider value={contextValue}>
      {children}
    </SystemSettingsContext.Provider>
  );
};

// Custom hook to use system settings context
export const useSystemSettings = () => {
  const context = useContext(SystemSettingsContext);
  if (!context) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
  }
  return context;
};

export default SystemSettingsContext;
